import { type FC, useState, useRef, useMemo, type ChangeEvent, useEffect } from "react";
import { type OptionProps } from "@root/shared/components/SelectBox";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { fetchAiMessage } from "./utils/fetchAiMessage";
import { getConfirmModalProps } from "./utils/getConfirmModalProps";
import { Presentation } from "./Presentation";
import {
  type PremiumScoutMessageAreaContainerProps,
  type PremiumScoutMessageAreaHandlers,
  type PremiumScoutMessageAreaModals,
  type DisableButtonText,
} from "./type";

export const PremiumScoutMessageArea: FC<PremiumScoutMessageAreaContainerProps> = ({
  isSp,
  isApp,
  templates,
  noticePointOptions,
  createTextUrl,
  optionPageUrl,
  initialTotalAiScoutMessageCounts,
  userInfoShortage,
  normalPremiumScoutCountShortage,
  nationalPremiumScoutCountShortage,
}) => {
  /* ===== UI 状態 ===== */
  const [isShowLoadingModal, setIsShowLoadingModal] = useState(false);
  const [isShowSupplementModal, setIsShowSupplementModal] = useState(false);
  const [isShowAiCreateConfirmModal, setIsShowAiCreateConfirmModal] = useState(false);
  const [isShowOverwriteTextAreaConfirmModal, setIsShowOverwriteTextAreaConfirmModal] =
    useState(false);
  const [isShowReloadModal, setIsShowReloadModal] = useState(false);
  const [isShowRefetchModal, setIsShowRefetchModal] = useState(false);
  const hasDisableAiScoutMessageReason =
    userInfoShortage || normalPremiumScoutCountShortage || nationalPremiumScoutCountShortage;
  const [disableText, setDisableText] = useState<DisableButtonText>(
    hasDisableAiScoutMessageReason ? "AIでメッセージを作成できません" : "",
  );

  /* ===== テキストエリアおよびテンプレート選択状態 ===== */
  const [selectedValue, setSelectedValue] = useState("");
  const [textAreaValue, setTextAreaValue] = useState("");
  const [lastSelectedValue, setLastSelectedValue] = useState("");
  const [lastTextAreaValue, setLastTextAreaValue] = useState("");
  const [pendingValue, setPendingValue] = useState("");
  const [pendingTextValue, setPendingTextValue] = useState("");
  const [lastTextIsAiCreated, setLastTextIsAiCreated] = useState(false);
  const [aiUsed, setAiUsed] = useState(false);
  const [totalAiScoutMessageCounts, setTotalAiScoutMessageCounts] = useState(
    initialTotalAiScoutMessageCounts,
  );

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  /* ========= テキストの値のフォーマット関数 ========= */
  const formatForTextArea = (value: string): string =>
    value.includes("|") ? value.split("|")[1] : "";

  /* ========= セレクトボックス用データ ========= */
  const options: OptionProps[] = [
    {
      label: "── 自分で作成したテンプレート ──",
      value: "separator-templates",
      disabled: true,
    },
    ...templates.map((tpl) => ({
      label: tpl.subject,
      value: `${tpl.id}|${tpl.message}`,
    })),
    {
      label: "── 定型文 ──",
      value: "separator-notices",
      disabled: true,
    },
    ...noticePointOptions.map(([val, label]) => ({
      label,
      value: `static|${val}`,
    })),
  ];

  // ユーザーに対し表示する理由説明は、上から順に優先度が高い
  // ex.求職者情報が足りない 尚且つ プレミアムスカウト残数がない場合、画面には求職者情報が足りないことを表示する
  const displayDisableReason = useMemo(() => {
    if (userInfoShortage) {
      return "userInfoShortage";
    }
    if (normalPremiumScoutCountShortage) {
      return "normalPremiumScoutCountShortage";
    }
    if (nationalPremiumScoutCountShortage) {
      return "nationalPremiumScoutCountShortage";
    }
    return undefined;
  }, [userInfoShortage, normalPremiumScoutCountShortage, nationalPremiumScoutCountShortage]);

  const dispatchTextAreaChangeEvent = () => {
    if (textAreaRef.current) {
      const event = new Event("change", { bubbles: true });
      textAreaRef.current.dispatchEvent(event);
    }
  };

  // NOTE: テキストエリアの値変更時に change イベントを強制発火させるため
  useEffect(() => {
    dispatchTextAreaChangeEvent();
  }, [textAreaValue]);

  /* ========= 「あなたをスカウトした理由」のセレクトボックスの値を変更した際 ========= */
  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    if (lastTextIsAiCreated && textAreaValue) {
      setPendingValue(value);
      setPendingTextValue(formatForTextArea(value));
      setIsShowOverwriteTextAreaConfirmModal(true);
    } else {
      setSelectedValue(value);
      setTextAreaValue(formatForTextArea(value));
      dispatchTextAreaChangeEvent();
      setLastSelectedValue(value);
      setLastTextAreaValue(formatForTextArea(value));
      setLastTextIsAiCreated(false);
      setDisableText(hasDisableAiScoutMessageReason ? "AIでメッセージを作成できません" : "");
    }
  };

  /* ========= 「あなたをスカウトした理由」の自由記入欄（テキストエリア）の値を変更した際 ========= */
  const handleTextAreaChange = (value: string) => {
    setTextAreaValue(value);
    setLastTextIsAiCreated(false);
    setDisableText(hasDisableAiScoutMessageReason ? "AIでメッセージを作成できません" : "");
  };

  /* ========= AIでのメッセージ作成を実行するか（使用可能回数がない場合：購入画面へいくか） の確認モーダル ========= */
  // 確認モーダルOPEN
  const handleClickOpenAiCreateConfirmModal = () => {
    setIsShowAiCreateConfirmModal(true);
  };
  // 確認モーダルCLOSE/CANCEL
  const handleClickCloseAiCreateConfirmModal = () => {
    setIsShowAiCreateConfirmModal(false);
    checkWordCount();
  };
  // AI作成「実行する」押下時
  const handleClickAiCreateTrigger = () => {
    setIsShowAiCreateConfirmModal(false);
    // ボタンが押せてしまうUI不整合のバグがあった場合を考慮・基本的に起きない想定
    if (userInfoShortage) {
      alert(
        "こちらの求職者は入力情報が足りず、AIでメッセージを作成できません。\n\nプロフィールの以下の項目を、50字以上記入済みの求職者に対して作成できます。\n自己PR／この業界を志した理由／大切にしていること／今後チャレンジしたいことのうち１つ",
      );
      return;
    }
    if (normalPremiumScoutCountShortage) {
      alert("プレミアムスカウトの残数がないため、AIでメッセージを作成できません。");
      return;
    }
    if (nationalPremiumScoutCountShortage) {
      alert("国家のプレミアムスカウトの残数がないため、AIでメッセージを作成できません。");
      return;
    }
    setIsShowLoadingModal(true);
    fetchAiMessage(createTextUrl)
      .then((data) => {
        if (data.error_status) {
          if (data.error_status === "system_error") {
            // NOTE: ajax通信そのものは成功したが、awsか何かの問題が発生して文章生成に失敗した場合ここに入る想定
            // リロードではなく再度リクエストを試す
            setIsShowRefetchModal(true);
          } else {
            // NOTE:フロントのボタンの活性・非活性の状況と、サーバ側の状況が一致していない場合にここに入る想定
            // 再度リクエストを走らせても結果は同じであるため、リロードを促す
            setIsShowReloadModal(true);
          }
        } else {
          // 取得したテキストをテキストエリアに表示
          setTextAreaValue(data.text);
          // 上書きキャンセルに対応するため
          setLastTextAreaValue(data.text);
          // プレミアムスカウト送信時に、バックエンドにフラグを送信する必要があるため
          setAiUsed(true);
          // changeイベントを発火させる
          dispatchTextAreaChangeEvent();
          // 最新のAI使用可能回数をバックエンドから受け取り、保持する（リロードせず連続使用された場合でも、正しい使用可能回数を表示するため）
          setTotalAiScoutMessageCounts(data.total_count);
          // 確認モーダルの分岐に関わるため、現在（最後）の値がAIで作成されたものかどうか判断できるようにする
          setLastTextIsAiCreated(true);
          // ユーザに表示するdisableボタンのテキストを更新
          setDisableText("作成済み");
          // プルダウンの値はリセットする
          setSelectedValue("");
          setLastSelectedValue("");
          // 前回システムエラーだった等ですでにモーダルが表示されている場合、閉じるため
          setIsShowRefetchModal(false);
          setIsShowReloadModal(false);
        }
      })
      .catch((error: Error) => {
        sendErrorLog(
          new Error(error.message) || "AIスカウトメッセージ作成リクエストに失敗しました。",
        );
        // NOTE: リロードではなく再度リクエストを試す
        setIsShowRefetchModal(true);
      })
      .finally(() => {
        setIsShowLoadingModal(false);
      });
  };
  // 「購入画面へ進む」押下時
  const handleClickTransitionToPurchaseOption = () => {
    window.open(optionPageUrl);
  };

  /* ========= テキストエリアの値を上書きして問題ないか の確認モーダル ========= */
  // 確認モーダルOPEN はテキストエリアの値を上書きする操作が起こった時に発生
  // 確認モーダルCLOSE/CANCEL
  const handleClickCloseOverwriteTextAreaConfirmModal = () => {
    setIsShowOverwriteTextAreaConfirmModal(false);
    setSelectedValue(lastSelectedValue);
    setTextAreaValue(lastTextAreaValue);
    checkWordCount();
  };
  // 上書き「実行する」押下時
  const handleClickOverwriteTextAreaTrigger = () => {
    setIsShowOverwriteTextAreaConfirmModal(false);
    setSelectedValue(pendingValue);
    setTextAreaValue(pendingTextValue);
    setLastTextIsAiCreated(false);
    setDisableText(hasDisableAiScoutMessageReason ? "AIでメッセージを作成できません" : "");
  };

  useEffect(() => {
    checkWordCount();
  }, [textAreaValue, selectedValue]);

  const checkWordCount = () => {
    // NOTE: setTimeout を使って、テキストエリアの値が変わった後に jQuery InputWordCounter を再実行する。
    // FIXME: jQueryから機能を剥がし、React内で文字数カウントが機能するように修正を検討してください。
    // 注意事項: 修正する際は、改行・スペースが文字数に適切に含まれているかなど、「見えている文字数」と「サーバ側に送った文字数」が一致しているかについて、注意する必要があります。
    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Counter = (window as any).InputWordCounter;
      if (typeof Counter === "function") {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
        new Counter().setCounters();
      }
    }, 0);
  };

  /* ========= なぜAIでメッセージを作成する機能が使用できないかを説明するモーダル ========= */
  // OPEN
  const handleClickOpenSupplementModal = () => {
    setIsShowSupplementModal(true);
  };
  // CLOSE
  const handleClickCloseSupplementModal = () => {
    setIsShowSupplementModal(false);
  };

  /* ========= システムエラーのためリロードを促すモーダル ========= */
  // OPEN
  const handleClickOpenReloadModal = () => {
    setIsShowReloadModal(true);
  };
  // CLOSE
  const handleClickCloseReloadModal = () => {
    setIsShowReloadModal(false);
  };
  // 「再読み込みする」押下時
  const handleClickReloadTrigger = () => {
    window.location.reload();
  };

  /* ========= システムエラーのため再フェッチを促すモーダル ========= */
  // OPEN
  const handleClickOpenRefetchModal = () => {
    setIsShowRefetchModal(true);
  };
  // CLOSE
  const handleClickCloseRefetchModal = () => {
    setIsShowRefetchModal(false);
  };
  // 「再度試す」押下時
  const handleClickRefetchTrigger = () => {
    handleClickAiCreateTrigger();
  };

  const {
    aiCreateConfirmModalProps,
    overwriteTextAreaConfirmModalProps,
    reloadModalProps,
    refetchModalProps,
  } = getConfirmModalProps({
    isApp,
    totalAiScoutMessageCounts,
    textAreaValue,
    lastTextIsAiCreated,
    handlers: {
      handleClickCloseAiCreateConfirmModal,
      handleClickAiCreateTrigger,
      handleClickTransitionToPurchaseOption,
      handleClickCloseOverwriteTextAreaConfirmModal,
      handleClickOverwriteTextAreaTrigger,
      handleClickCloseReloadModal,
      handleClickReloadTrigger,
      handleClickCloseRefetchModal,
      handleClickRefetchTrigger,
    },
    visibility: {
      isShowAiCreateConfirmModal,
      isShowOverwriteTextAreaConfirmModal,
      isShowReloadModal,
      isShowRefetchModal,
    },
  });

  const handlers: PremiumScoutMessageAreaHandlers = {
    handleSelectChange,
    handleTextAreaChange,
    handleClickCloseAiCreateConfirmModal,
    handleClickCloseOverwriteTextAreaConfirmModal,
    handleClickTransitionToPurchaseOption,
    handleClickOverwriteTextAreaTrigger,
    handleClickOpenAiCreateConfirmModal,
    handleClickOpenSupplementModal,
    handleClickCloseSupplementModal,
    handleClickAiCreateTrigger,
    handleClickOpenReloadModal,
    handleClickCloseReloadModal,
    handleClickOpenRefetchModal,
    handleClickCloseRefetchModal,
    handleClickReloadTrigger,
    handleClickRefetchTrigger,
  };

  const modals: PremiumScoutMessageAreaModals = {
    aiCreateConfirm: aiCreateConfirmModalProps,
    overwriteTextAreaConfirm: overwriteTextAreaConfirmModalProps,
    reload: reloadModalProps,
    refetch: refetchModalProps,
  };

  return (
    <Presentation
      isSp={isSp}
      selectedValue={selectedValue}
      textAreaValue={textAreaValue}
      totalAiScoutMessageCounts={totalAiScoutMessageCounts}
      disableText={disableText}
      displayDisableReason={displayDisableReason}
      options={options}
      textAreaRef={textAreaRef}
      hasDisableAiScoutMessageReason={hasDisableAiScoutMessageReason}
      lastTextIsAiCreated={lastTextIsAiCreated}
      aiUsed={aiUsed}
      isShowSupplementModal={isShowSupplementModal}
      isShowLoadingModal={isShowLoadingModal}
      handlers={handlers}
      modals={modals}
    />
  );
};
